import {
  accessTokenAtom,
  tokenExpiresAtAtom,
  isInitializingAtom,
  isLoadingAtom,
  authErrorAtom,
  twoFactorTokenAtom,
  userJwtAtom,
  isAuthenticatedAtom,
  isOfflineAtom,
  accessTokenSyncAtom,
} from "./atoms"
import { AuthError, AuthTokens, MagicLinkResult, UserJwt } from "./types"
import { client } from "@/heyapi/client.gen"
import { Client } from "@/heyapi/client"
import {
  Auth,
  LoginRequest,
  UserRegister,
  MagicLinkRequest,
} from "@/heyapi"
import { jwtDecode } from "jwt-decode"
import {
  decodeJWT,
  getExpiresAtSeconds,
  mapApiErrorToAuthError,
} from "@/auth/utils"
import { QueryClient } from "@tanstack/react-query"

import { usersReadUserMeQueryKey } from "@/heyapi/@tanstack/react-query.gen"
import { Store } from "jotai/vanilla/store"
import { fetchAccessToken, rememberAccessSignIn } from "./cloudflareAccess"
import { safeError } from "@/utils/safeError"

export class AuthService {
  private client: Client
  private store: any // Jotai store
  private queryClient: QueryClient | null = null
  private refreshPromise: Promise<void> | null = null
  private onlineHandler: (() => void) | null = null
  private consecutivePingFailures: number = 0
  private readonly MAX_PING_FAILURES = 2 // Log out after 2 consecutive failures

  constructor(queryClient: QueryClient, store: Store) {
    this.client = client
    this.store = store
    this.queryClient = queryClient

    // Set up API client auth and credentials
    this.client.setConfig({
      auth: () => {
        const accessToken = this.store.get(accessTokenSyncAtom)
        return accessToken || ""
      },
      credentials: "include", // Include cookies in requests
    })

    // Set up online event handler for network recovery
    this.setupNetworkRecovery()
  }

  // Initialize auth state with health check system
  async initialize(): Promise<void> {
    console.log("Initializing auth state")
    this.store.set(isInitializingAtom, true)
    this.store.set(authErrorAtom, null)

    try {
      const accessToken = await this.store.get(accessTokenAtom)
      const expiresAt = await this.store.get(tokenExpiresAtAtom)
      const user = await this.store.get(userJwtAtom)
      if (accessToken && expiresAt && user) {
        // We have tokens, verify they're valid with the server
        try {
          // Set authenticated state after successful ping
          this.store.set(isAuthenticatedAtom, true)

          // Check if token needs refresh and do it if needed
          if (this.needsRefresh(expiresAt)) {
            console.log("Token needs refresh, refreshing...")
            await this.refreshTokenSilently()
          }

          // Start periodic health checks
          this.startHealthCheck()
        } catch (pingError) {
          console.warn("Token ping failed, clearing auth state:", safeError(pingError))
          this.clearAuthData()
        }
      } else if (
        !(await this.store.get(twoFactorTokenAtom)) &&
        (await this.signInWithCloudflareAccess())
      ) {
        console.log("Signed in through Cloudflare Access")
      } else {
        // No tokens, ensure we're marked as not authenticated
        this.store.set(isAuthenticatedAtom, false)
        console.log("No valid tokens found")
      }
    } catch (error) {
      console.error("Auth initialization failed:", safeError(error))
      const authError = mapApiErrorToAuthError(error)
      this.store.set(authErrorAtom, authError)
      this.clearAuthData()
    } finally {
      this.store.set(isInitializingAtom, false)
    }
  }

  // Magic link login
  // linkTarget: "admin" for admin panel (panel.condri.app), "site" for main site (condri.app)
  async requestMagicLink(
    email: string,
    linkTarget: "site" | "admin" = "admin",
  ): Promise<void> {
    this.store.set(isLoadingAtom, true)
    this.store.set(authErrorAtom, null)

    try {
      const { data, error } = await Auth.authRequestMagicLink({
        body: { email, link_target: linkTarget },
      })
      if (error) {
        throw error
      }
      // Magic link sent successfully - no need to set tokens yet
      console.log("Magic link sent")
    } catch (error) {
      console.error("Magic link request failed:", safeError(error))
      const authError = mapApiErrorToAuthError(error)
      this.store.set(authErrorAtom, authError)
      throw authError
    } finally {
      this.store.set(isLoadingAtom, false)
    }
  }

  // Verify magic link token
  /**
   * Verify a magic link.
   *
   * Three outcomes, and they used to collapse into two: a 2FA-protected
   * account verifies *successfully* and is then asked for its second factor,
   * but that returned `null` — the same value as a genuine failure — so the
   * verification page told people their working link was expired or
   * malformed. Every admin sign-in hit it, because TOTP is required for
   * admin. Hence a discriminated result rather than a nullable one.
   */
  async verifyMagicLink(token: string): Promise<MagicLinkResult> {
    this.store.set(isLoadingAtom, true)
    this.store.set(authErrorAtom, null)

    try {
      const { data, error } = await Auth.authVerifyMagicLink({
        body: { token },
      })
      if (error) {
        throw error
      }

      const result = this.applyPasswordlessSignIn(data)
      if (result.status !== "failed") return result
    } catch (error) {
      console.error("Magic link verification failed:", safeError(error))
      const authError = mapApiErrorToAuthError(error)
      this.store.set(authErrorAtom, authError)
      throw authError
    } finally {
      this.store.set(isLoadingAtom, false)
    }
    return { status: "failed" }
  }

  /**
   * Silent panel sign-in on the Cloudflare Access identity. Never surfaces an
   * error: any failure leaves the magic-link login exactly as it was.
   */
  private async signInWithCloudflareAccess(): Promise<boolean> {
    const token = await fetchAccessToken()
    if (!token) return false
    try {
      const { data, error } = await Auth.authCloudflareAccessLogin({
        body: { token },
      })
      if (error) return false
      const result = this.applyPasswordlessSignIn(data)
      if (result.status === "failed") return false
      rememberAccessSignIn(true)
      return true
    } catch (error) {
      console.warn("Cloudflare Access sign-in failed:", safeError(error))
      return false
    }
  }

  // Shared by the magic link and Cloudflare Access: both answer with a
  // session, a 2FA challenge, or (for a pending deletion) neither.
  private applyPasswordlessSignIn(data: unknown): MagicLinkResult {
    if (!data || typeof data !== "object") return { status: "failed" }
    if (
      "two_factor_token" in data &&
      typeof data.two_factor_token === "string" &&
      data.two_factor_token
    ) {
      this.setAuthTokens({
        access_token: null,
        expires_at: null,
        two_factor_token: data.two_factor_token,
      })
      return { status: "two_factor" }
    }
    if (
      "access_token" in data &&
      typeof data.access_token === "string" &&
      data.access_token
    ) {
      const token_data = decodeJWT(data.access_token)
      this.setAuthTokens({
        access_token: data.access_token,
        expires_at: token_data.exp,
        two_factor_token: null,
      })
      return { status: "signed_in", user: token_data.user_data }
    }
    return { status: "failed" }
  }

  // Register with email/password
  async register(credentials: UserRegister): Promise<void> {
    this.store.set(isLoadingAtom, true)
    this.store.set(authErrorAtom, null)

    try {
      const { error } = await Auth.authRegisterUser({
        body: credentials,
      })
      if (error) {
        this.store.set(authErrorAtom, {
          type: "invalid_credentials",
          message: error.detail || "Invalid credentials",
          retryable: false,
        })
        throw error
      }
    } catch (error) {
      const authError = mapApiErrorToAuthError(error)
      this.store.set(authErrorAtom, authError)
      throw authError
    } finally {
      this.store.set(isLoadingAtom, false)
    }
  }

  // Login with email/password
  async login(credentials: LoginRequest): Promise<void> {
    this.store.set(isLoadingAtom, true)
    this.store.set(authErrorAtom, null)

    try {
      const { data, error } = await Auth.authLogin({
        body: credentials,
      })
      if (error) {
        this.store.set(authErrorAtom, {
          type: "invalid_credentials",
          message: error.detail || "Invalid credentials",
          retryable: false,
        })
        throw error
      }
      // Either AccessToken or TwoFactorToken or Message saying check your email.
      if ("access_token" in data) {
        // AccessToken response
        const { access_token } = data
        this.setAuthTokens({ access_token })
      } else if ("two_factor_token" in data) {
        // TwoFactorToken response - user needs to provide TOTP code
        const { two_factor_token } = data
        this.store.set(twoFactorTokenAtom, two_factor_token)
        // this.store.set(authErrorAtom, { type: 'totp_required', message: 'Please enter your 2FA code to complete login', retryable: false });
        // Don't throw error here - this is a normal part of 2FA flow
        return
      } else if ("message" in data) {
        // Message response (email not verified)
        const { message } = data
        this.store.set(authErrorAtom, {
          type: "email_not_verified",
          message: message,
          retryable: false,
        })
        throw new Error(message) // Throw so UI knows login didn't succeed
      }
    } catch (error) {
      const authError = mapApiErrorToAuthError(error)
      this.store.set(authErrorAtom, authError)
      throw error
    } finally {
      this.store.set(isLoadingAtom, false)
    }
  }

  // Complete 2FA authentication with TOTP code
  async verifyTwoFactor(totpCode: string): Promise<void> {
    this.store.set(isLoadingAtom, true)
    this.store.set(authErrorAtom, null)

    const twoFactorToken = this.store.get(twoFactorTokenAtom)
    if (!twoFactorToken) {
      const error: AuthError = {
        type: "unknown",
        message: "No 2FA token available",
        retryable: false,
      }
      this.store.set(authErrorAtom, error)
      throw error
    }

    try {
      const { data, error } = await Auth.authVerifyTotp({
        body: {
          two_factor_token: twoFactorToken,
          totp_code: totpCode,
        },
      })
      if (error) {
        throw error
      }
      if ("access_token" in data) {
        // Success - clear 2FA token and set auth tokens
        this.store.set(twoFactorTokenAtom, null)
        const { access_token } = data
        this.setAuthTokens({ access_token })
      } else {
        throw new Error("Invalid 2FA response format")
      }
    } catch (error) {
      const authError = mapApiErrorToAuthError(error)
      this.store.set(authErrorAtom, authError)
      throw error
    } finally {
      this.store.set(isLoadingAtom, false)
    }
  }

  // Refresh token
  async refreshTokenSilently(): Promise<void> {
    // Prevent multiple concurrent refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise
    }
    this.refreshPromise = this._refreshToken()
    try {
      await this.refreshPromise
    } finally {
      this.refreshPromise = null
    }
  }

  private async _refreshToken(): Promise<void> {
    console.log("Refreshing token _refreshToken")
    try {
      // Simplified: No need to get refresh token from storage
      // The refresh token is automatically sent as HTTP-only cookie
      const accessToken = this.store.get(accessTokenAtom)
      if (!accessToken) {
        // Not logged in so no need to refresh.
        throw new Error("No valid session found")
      }
      const { error, data } = await Auth.authRefreshAccessToken()
      if (error) {
        console.log("Error refreshing token", safeError(error))
        throw error
      }
      console.log("Token refreshed")
      const { access_token } = data
      // New refresh token is automatically set as HTTP-only cookie by backend
      this.setAuthTokens({ access_token })
    } catch (error) {
      this.clearAuthData()
      this.clearQueryCache()
    }
  }

  // Clear error (for UI error dismissal)
  clearError(): void {
    this.store.set(authErrorAtom, null)
  }

  // Utility methods
  private setAuthTokens(tokens: AuthTokens): void {
    // Jotai atomWithStorage handles persistence automatically
    // Sync atoms are derived automatically - no manual sync needed
    this.store.set(accessTokenAtom, tokens.access_token)

    if (tokens.access_token) {
      const token_data = decodeJWT(tokens.access_token)
      this.store.set(tokenExpiresAtAtom, token_data.exp)
      this.store.set(userJwtAtom, token_data.user_data)
      // Manually set authenticated state when we receive valid tokens
      this.store.set(isAuthenticatedAtom, true)

      // Start health check system for new authentication
      this.startHealthCheck()
    }
    this.store.set(twoFactorTokenAtom, tokens.two_factor_token)
  }

  clearAuthData(): void {
    // Stop health check when clearing auth data
    this.stopHealthCheck()

    // Reset ping failure counter
    this.consecutivePingFailures = 0

    // Jotai atomWithStorage handles persistence automatically
    // Sync atoms are derived automatically - no manual sync needed
    rememberAccessSignIn(false)
    this.store.set(userJwtAtom, null)
    this.store.set(accessTokenAtom, null)
    this.store.set(tokenExpiresAtAtom, null)
    this.store.set(twoFactorTokenAtom, null)
    this.store.set(authErrorAtom, null)

    // Manually clear authenticated state
    this.store.set(isAuthenticatedAtom, false)
  }

  // Clear React Query cache for user-related data
  private clearQueryCache(): void {
    // Invalidate all user-related queries
    this.queryClient.invalidateQueries({ queryKey: usersReadUserMeQueryKey() })
    // Also remove the data from cache immediately
    this.queryClient.removeQueries({ queryKey: usersReadUserMeQueryKey() })
  }

  isTokenExpired(expiresAt: number): boolean {
    // Expires in less than 1 second
    return (getExpiresAtSeconds(expiresAt)) < 1
  }

  // Get current access token (auto-refresh if needed)
  async getAccessToken(): Promise<string | null> {
    const accessToken = this.store.get(accessTokenAtom)
    const expiresAt = this.store.get(tokenExpiresAtAtom)

    if (!accessToken || !expiresAt) return null

    if (this.isTokenExpired(expiresAt)) {
      await this.refreshTokenSilently()
      return this.store.get(accessTokenAtom)
    }

    return accessToken
  }

  // Health check methods
  private healthCheckInterval: number | null = null

  // Check if token needs refresh (expires in next 5 minutes)
  needsRefresh(expiresAt: number): boolean {
    const seconds = getExpiresAtSeconds(expiresAt)
    // const minutes = Math.round(seconds / 60)
    // console.log(`Token needs refresh in: ${minutes}m${seconds % 60}s`)
    return seconds <= 30
  }

  // Ping server to verify token validity
  async pingServer(): Promise<void> {
    // Skip ping if offline
    const isOffline = this.store.get(isOfflineAtom)
    const isAuthenticated = this.store.get(isAuthenticatedAtom)
    if (isOffline) {
      console.log("Skipping ping - app is offline")
      return
    }
    if (!isAuthenticated) {
      console.log("Skipping ping - authed:", isAuthenticated)
      this.logout()
      return
    }
    console.log("Pinging health check server")
    try {
      const { data, error } = await Auth.authPingAuth()
      if (error) {
        throw new Error(`Ping failed: ${error}`)
      }
      // Ping successful - reset failure counter
      this.consecutivePingFailures = 0
      // console.log("Auth ping successful:", data)
    } catch (error) {
      // Check if this might be a network error
      if (
        error instanceof Error &&
        (error.message.includes("fetch") ||
          error.message.includes("network") ||
          error.message.includes("Failed to fetch"))
      ) {
        console.log(
          "Ping failed due to network error (likely offline):",
          error.message,
        )
        // Don't throw for network errors - just log them
        return
      }
      // Re-throw for actual auth errors (401, etc.)
      throw error
    }
  }

  // Start periodic health check (ping every 5 minutes)
  startHealthCheck(): void {
    // Clear any existing interval
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
    }

    // Start new interval
    this.healthCheckInterval = window.setInterval(
      async () => {
        try {
          const expiresAt = this.store.get(tokenExpiresAtAtom)
          const isOffline = this.store.get(isOfflineAtom)

          if (isOffline) {
            console.log("Skipping health check - app is offline")
            return
          }

          // Check if token needs refresh first
          if (this.needsRefresh(expiresAt)) {
            console.log("Token needs refresh during health check...")
            await this.refreshTokenSilently()
          }

          // Ping server to verify token is still valid
          await this.pingServer()
        } catch (error) {
          // Ping failed - try to refresh token before logging out
          console.warn(
            `Health check ping failed (attempt ${this.consecutivePingFailures + 1}/${this.MAX_PING_FAILURES}):`,
            safeError(error),
          )

          // Try to refresh token first
          try {
            console.log("Attempting token refresh after ping failure...")
            await this.refreshTokenSilently()
            // If refresh succeeded, try ping again
            try {
              await this.pingServer()
              // If ping succeeds after refresh, we're good
              console.log("Ping succeeded after token refresh")
              return
            } catch (refreshPingError) {
              // Even after refresh, ping still fails
              console.warn(
                "Ping still failed after token refresh:",
                safeError(refreshPingError),
              )
              this.consecutivePingFailures++
            }
          } catch (refreshError) {
            // Token refresh also failed
            console.warn(
              "Token refresh failed after ping failure:",
              safeError(refreshError),
            )
            this.consecutivePingFailures++
          }

          // Only log out after multiple consecutive failures
          if (this.consecutivePingFailures >= this.MAX_PING_FAILURES) {
            console.error(
              `Health check failed ${this.consecutivePingFailures} times, clearing auth state`,
            )
            this.clearAuthData()
            this.stopHealthCheck()
          }
        }
      },
      5 * 60 * 1000,
    ) // 5 minutes
    // console.log("Health check started (5 minute intervals)")
  }

  // Stop periodic health check
  stopHealthCheck(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
      this.healthCheckInterval = null
      console.log("Health check stopped")
    }
  }

  // Logout (with health check cleanup)
  async logout(): Promise<void> {
    this.store.set(authErrorAtom, null)
    this.stopHealthCheck() // Stop health check on logout
    this.cleanup() // Clean up network handlers

    try {
      await Auth.authLogout()
    } catch (error) {
      // Continue with local logout even if API call fails
    }
    this.clearAuthData()
    this.clearQueryCache()
  }

  // Network recovery setup
  private setupNetworkRecovery(): void {
    if (typeof window === "undefined") return

    this.onlineHandler = async () => {
      console.log("Network recovered - attempting auth recovery")
      const accessToken = this.store.get(accessTokenAtom)
      const isAuthenticated = this.store.get(isAuthenticatedAtom)

      if (accessToken && isAuthenticated) {
        try {
          // Try to refresh token and ping server when coming back online
          const expiresAt = this.store.get(tokenExpiresAtAtom)
          if (this.needsRefresh(expiresAt)) {
            console.log("Refreshing token after network recovery...")
            await this.refreshTokenSilently()
          }

          // Ping server to verify auth state
          await this.pingServer()
          console.log("Auth recovery successful")
        } catch (error) {
          // Try to refresh token before giving up
          try {
            console.log("Attempting token refresh after network recovery ping failure...")
            await this.refreshTokenSilently()
            // Try ping again after refresh
            await this.pingServer()
            console.log("Auth recovery successful after token refresh")
          } catch (refreshError) {
            console.error("Auth recovery failed after token refresh:", safeError(refreshError))
            this.consecutivePingFailures++
            // Only log out if we've had multiple failures
            if (this.consecutivePingFailures >= this.MAX_PING_FAILURES) {
              this.clearAuthData()
            }
          }
        }
      }
    }

    window.addEventListener("online", this.onlineHandler)
  }

  // Clean up network handlers
  private cleanup(): void {
    if (typeof window !== "undefined" && this.onlineHandler) {
      window.removeEventListener("online", this.onlineHandler)
      this.onlineHandler = null
    }
  }
}
